import { Component, inject, OnInit } from '@angular/core'
import { CommonModule } from '@angular/common'
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms'
import { CardModule } from 'primeng/card'
import { ButtonModule } from 'primeng/button'
import { InputNumberModule } from 'primeng/inputnumber'
import { TooltipModule } from 'primeng/tooltip'
import { ProgressSpinnerModule } from 'primeng/progressspinner'
import { forkJoin } from 'rxjs'
import { SettingService } from '../../services/setting.service'
import { SettingModel } from '../../../../core/models/setting.model'
import { ToastService } from '../../../../shared/services/toast.service'

interface SettingMeta {
  label: string
  icon: string
  unit: string
  min: number
  max: number
}

const SETTING_META: Record<string, SettingMeta> = {
  tablot_default_service_duration: {
    label: 'Durée de service par défaut',
    icon: 'pi pi-clock',
    unit: 'secondes',
    min: 300,
    max: 28800,
  },
  tablot_workday_start_hour: {
    label: 'Heure de début de journée',
    icon: 'pi pi-sun',
    unit: 'h',
    min: 0,
    max: 12,
  },
  tablot_workday_end_hour: {
    label: 'Heure de fin de journée',
    icon: 'pi pi-moon',
    unit: 'h',
    min: 12,
    max: 23,
  },
  tablot_max_technician_radius_km: {
    label: 'Rayon max technicien',
    icon: 'pi pi-map-marker',
    unit: 'km',
    min: 10,
    max: 1000,
  },
  tablot_optimal_slots_count: {
    label: 'Nombre de créneaux optimaux',
    icon: 'pi pi-list',
    unit: 'créneaux',
    min: 1,
    max: 20,
  },
  tablot_max_scan_working_days: {
    label: 'Jours ouvrés à scanner',
    icon: 'pi pi-calendar',
    unit: 'jours',
    min: 1,
    max: 90,
  },
  tablot_cluster_radius_km: {
    label: 'Rayon de clustering',
    icon: 'pi pi-th-large',
    unit: 'km',
    min: 10,
    max: 500,
  },
  tablot_vehicle_start_buffer_minutes: {
    label: 'Marge de départ véhicule',
    icon: 'pi pi-car',
    unit: 'min',
    min: 0,
    max: 240,
  },
  tablot_vehicle_end_buffer_minutes: {
    label: 'Marge de retour domicile',
    icon: 'pi pi-home',
    unit: 'min',
    min: 0,
    max: 240,
  },
}

@Component({
  selector: 'app-tablot-settings',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    CardModule,
    ButtonModule,
    InputNumberModule,
    TooltipModule,
    ProgressSpinnerModule,
  ],
  templateUrl: './tablot-settings.component.html',
  styleUrl: './tablot-settings.component.scss',
})
export class TablotSettingsComponent implements OnInit {
  private settingService = inject(SettingService)
  private toastService = inject(ToastService)
  private fb = inject(FormBuilder)

  public settings$ = this.settingService.settings$
  public loading$ = this.settingService.loading$
  public saving$ = this.settingService.saving$

  public form!: FormGroup
  public settings: SettingModel[] = []
  public loading = true
  public saving = false

  readonly settingMeta = SETTING_META

  ngOnInit() {
    this.settingService.getByType('tablot').subscribe({
      next: (settings) => {
        this.settings = settings
        this.loading = false
        this.buildForm(settings)
      },
      error: () => {
        this.loading = false
        this.toastService.showError(
          'Impossible de charger les paramètres Tablot'
        )
      },
    })
  }

  private buildForm(settings: SettingModel[]) {
    const controls: Record<string, unknown> = {}
    for (const s of settings) {
      controls[s.key] = [s.value, [Validators.required, Validators.min(0)]]
    }
    this.form = this.fb.group(controls)
  }

  getMeta(key: string): SettingMeta {
    return (
      SETTING_META[key] ?? {
        label: key,
        icon: 'pi pi-cog',
        unit: '',
        min: 0,
        max: 999999,
      }
    )
  }

  saveAll() {
    if (this.form.invalid) return

    this.saving = true
    const requests = this.settings.map((s) =>
      this.settingService.update(s.id, this.form.value[s.key])
    )

    forkJoin(requests).subscribe({
      next: () => {
        this.saving = false
        this.toastService.showSuccess('Paramètres sauvegardés avec succès')
        // Met à jour les valeurs de référence
        for (const s of this.settings) {
          s.value = this.form.value[s.key]
        }
      },
      error: () => {
        this.saving = false
        this.toastService.showError(
          'Erreur lors de la sauvegarde des paramètres'
        )
      },
    })
  }

  get isDirty(): boolean {
    if (!this.form) return false
    return this.settings.some((s) => this.form.value[s.key] !== s.value)
  }

  resetForm() {
    if (!this.form) return
    for (const s of this.settings) {
      this.form.get(s.key)?.setValue(s.value)
    }
  }
}
